/* @flow */
import {createSelector} from 'reselect';

import type {NutshellState} from '../../store';

import type {OnboardingState} from './onboarding-reducer';
import {joyrideSteps, isJoyrideADemo} from './onboarding-constants';
import type {JoyrideId, Step} from './onboarding-constants';

const getOnboardingState = (state: NutshellState): OnboardingState => state.onboarding;
const getJoyrides = (state: NutshellState) => getOnboardingState(state).joyrides;
const getJoyrideIds = (state: NutshellState) =>
    Object.keys(getJoyrides(state)).filter((id) => id !== null);

export const getIsRehydrated = (state: NutshellState): boolean =>
    getOnboardingState(state).isRehydrated;
export const getIsEnabled = (state: NutshellState): boolean => getOnboardingState(state).isEnabled;
export const getActiveJoyrideId = (state: NutshellState): JoyrideId =>
    getOnboardingState(state).activeJoyrideId;
export const getIsJoyrideTemporarilyStopped = (state: NutshellState): boolean =>
    getOnboardingState(state).isTemporarilyStopped;

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getActiveSteps = createSelector([getActiveJoyrideId], (activeJoyrideId: JoyrideId) => {
    if (!joyrideSteps[activeJoyrideId]) return;

    return joyrideSteps[activeJoyrideId];
});

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getActiveStepIndex = createSelector(
    [getJoyrides, getActiveJoyrideId],
    (joyrides, activeJoyrideId: JoyrideId): ?number => {
        if (!joyrides[activeJoyrideId]) return null;

        return joyrides[activeJoyrideId].stepIndex;
    }
);

export function getJoyrideStep(state: NutshellState, joyrideId: JoyrideId, index: number): ?Step {
    if (!joyrideSteps[joyrideId]) return undefined;

    return joyrideSteps[joyrideId][index];
}

export function getJoyrideStepIndex(state: NutshellState, joyrideId: JoyrideId): number {
    return getJoyrides(state)[joyrideId].stepIndex;
}

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getActiveStep = createSelector(
    [getActiveSteps, getActiveStepIndex],
    (activeSteps, activeStepIndex): ?Step => {
        if (!activeSteps) return;
        if (activeStepIndex === undefined || activeStepIndex === null) return;

        return activeSteps[activeStepIndex];
    }
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getIsAnyJoyrideActive = createSelector(
    [getJoyrides, getActiveJoyrideId],
    (joyrides, activeJoyrideId: JoyrideId): boolean => {
        return Boolean(joyrides[activeJoyrideId]);
    }
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getCompletedJoyrides = createSelector([getJoyrides], (joyrides) => {
    return Object.keys(joyrides).filter((id) => {
        const isNull = String(id) === 'null';
        const isComplete = joyrides[id].stepIndex >= joyrideSteps[id].length - 1;

        return !isNull && isComplete;
    });
});

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getUncompletedJoyrides = createSelector([getJoyrides], (joyrides) => {
    return Object.keys(joyrides).filter((id) => {
        const isNull = String(id) === 'null';
        const isComplete = joyrides[id].stepIndex >= joyrideSteps[id].length - 1;

        return !isNull && !isComplete;
    });
});

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getPausedJoyrides = createSelector(
    [getJoyrides, getCompletedJoyrides, getActiveJoyrideId],
    (joyrides, completedJoyrides, activeJoyrideId) => {
        return Object.keys(joyrides).filter((joyrideId) => {
            const isNull = String(joyrideId) === 'null';
            const isStarted = joyrides[joyrideId].stepIndex > 0;
            const isActive = joyrideId === activeJoyrideId;
            const isCompleted = completedJoyrides.includes(joyrideId);

            return !isNull && isStarted && !isActive && !isCompleted;
        });
    }
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getJoyrideTotalStepCounts = createSelector([getJoyrideIds], (joyrideIds) => {
    return joyrideIds.reduce((acc, id) => {
        if (String(id) !== 'null') {
            acc[id] = joyrideSteps[id].length;
        }

        return acc;
    }, {});
});

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getJoyrideCurrentStepIndexes = createSelector(
    [getJoyrides, getJoyrideIds],
    (joyrides, joyrideIds) => {
        return joyrideIds.reduce((acc, id) => {
            if (String(id) !== 'null') {
                acc[id] = joyrides[id].stepIndex;
            }

            return acc;
        }, {});
    }
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getNextJoyrideId = createSelector(
    [getJoyrides, getUncompletedJoyrides, getActiveJoyrideId],
    (joyrides, uncompletedJoyrides, activeJoyrideId: JoyrideId): ?JoyrideId => {
        const otherUncompletedJoyrides = Object.keys(joyrides).filter((joyrideId) => {
            return uncompletedJoyrides.includes(joyrideId) && joyrideId !== activeJoyrideId;
        });
        if (otherUncompletedJoyrides.length > 0) {
            // $FlowFixMe upgrading Flow to v0.92.1 on web
            return otherUncompletedJoyrides[0];
        } else {
            return null;
        }
    }
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getIsActiveJoyrideDemoTour = createSelector([getActiveJoyrideId], (activeJoyrideId) =>
    isJoyrideADemo(activeJoyrideId)
);

// $FlowFixMe upgrading Flow to v0.92.1 on web
export const getFinishButtonText = createSelector(
    [getNextJoyrideId, getIsActiveJoyrideDemoTour],
    (nextJoyrideId: ?JoyrideId, isDemoTourActive: boolean): string => {
        if (!isDemoTourActive) return 'Got it';

        // Hard-coding the return here, as we have a stubbed joyride
        // for reports which, though it's not live, is causing the
        // button text to read 'Next Tour'.
        return 'Nice job';
    }
);
